import React, { useMemo, useState } from 'react';
import { Wand2, Loader2, CheckCircle2, XCircle, AlertTriangle, ChevronDown } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import RefListInput from './RefListInput';
import { renderMarkdown } from '../services/markdown';
import { seoChecks } from './BrandSeoForm';
import FaqEditor from './FaqEditor';

const Counter = ({ value, max }) => <span className={`text-[11px] ${value > max ? 'text-red-600' : 'text-gray-400'}`}>{value}/{max}</span>;

// SEO fields shared by category admin form (H1, title, meta, excerpt, long text, tags)
const CategorySeoSection = ({ form, setForm, onGenerate, generating, urlPath, grade, GradeBadge, ScoreDetail }) => {
  const [open, setOpen] = useState(!!(form.h1 || form.description));
  const [preview, setPreview] = useState(false);
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const checks = useMemo(() => seoChecks({ ...form, slug: '', secondary_keywords: [] }, null).filter(c => !['slug', 'unique'].includes(c.id)), [form]);
  const score = checks.filter(c => c.ok).length;
  const words = (form.description || '').trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="border border-gray-200 rounded-lg" data-testid="category-seo-section">
      <button type="button" data-testid="category-seo-toggle" onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="text-sm font-semibold text-gray-900">Référencement (SEO) de la page catégorie</span>
        <span className="flex items-center gap-2">
          {grade && GradeBadge && <GradeBadge score={grade} testId="category-page-grade" />}
          <span data-testid="category-seo-score" className={`text-xs font-bold px-2 py-0.5 rounded-full ${score >= 7 ? 'bg-green-100 text-green-700' : score >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{score}/{checks.length}</span>
          <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
          {grade && ScoreDetail && <div className="bg-gray-50 rounded-md p-2" data-testid="category-page-score-detail"><p className="text-[11px] font-medium text-gray-600 mb-1">Note SEO de la page : {grade.grade} · {grade.points}/{grade.max} points · {grade.langs}/5 langues</p><ScoreDetail score={grade} testId="category-page-checks" /></div>}
          <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-xs text-gray-600 flex-1">Générer un texte type à partir du nom de la catégorie, de ses sous-catégories et des marques réellement présentes. À relire et personnaliser.</p>
            <Button type="button" data-testid="category-generate-button" onClick={onGenerate} disabled={generating || !onGenerate} variant="outline" size="sm" className="border-orange-400 text-orange-700 hover:bg-orange-100">
              {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Wand2 size={14} className="mr-1" />} Générer
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mot-clé principal</label>
              <Input data-testid="category-main-keyword-input" value={form.main_keyword} onChange={set('main_keyword')} placeholder="Freins pas cher" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
              <p className="text-sm text-gray-500 py-2 font-mono">{urlPath}</p>
            </div>
            <div>
              <div className="flex justify-between mb-1"><label className="text-xs font-medium text-gray-600">H1 (titre de la page)</label><Counter value={form.h1.length} max={120} /></div>
              <Input data-testid="category-h1-input" value={form.h1} onChange={set('h1')} placeholder="Freins pas cher : qualité d'origine au meilleur prix" />
            </div>
            <div>
              <div className="flex justify-between mb-1"><label className="text-xs font-medium text-gray-600">Titre SEO (onglet / Google)</label><Counter value={form.seo_title.length} max={70} /></div>
              <Input data-testid="category-seo-title-input" value={form.seo_title} onChange={set('seo_title')} placeholder="Freins pas cher | OTOPIECES" />
            </div>
            <div>
              <div className="flex justify-between mb-1"><label className="text-xs font-medium text-gray-600">Méta-description</label><Counter value={form.meta_description.length} max={160} /></div>
              <Textarea data-testid="category-meta-description-input" rows={3} value={form.meta_description} onChange={set('meta_description')} />
            </div>
            <div>
              <div className="flex justify-between mb-1"><label className="text-xs font-medium text-gray-600">Extrait court (sous le titre)</label><Counter value={form.excerpt.length} max={400} /></div>
              <Textarea data-testid="category-excerpt-input" rows={3} value={form.excerpt} onChange={set('excerpt')} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-600">Description longue <span className="text-gray-400">(Markdown · {words} mots · cible 250–400)</span></label>
              <button type="button" data-testid="category-description-preview-toggle" onClick={() => setPreview(v => !v)} className={`text-xs px-2 py-1 rounded border ${preview ? 'bg-orange-500 text-white border-orange-500' : 'text-gray-600 border-gray-300 hover:border-orange-400'}`}>{preview ? 'Éditer' : 'Aperçu'}</button>
            </div>
            {preview ? (
              <div data-testid="category-description-preview" className="prose-legal border border-gray-200 rounded-md p-4 min-h-[160px] text-sm text-gray-700 bg-gray-50" dangerouslySetInnerHTML={{ __html: renderMarkdown(form.description) }} />
            ) : (
              <Textarea data-testid="category-description-input" value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value.slice(0, 8000) }))} rows={10} className="text-sm" />
            )}
          </div>
          <RefListInput testId="category-tags" label="Tags (recherches associées + meta keywords)" placeholder="freins pas cher, disques de frein…" values={form.tags} onChange={(tags) => setForm(f => ({ ...f, tags }))} />
          <FaqEditor testId="category-faq" items={form.faq || []} onChange={(faq) => setForm(f => ({ ...f, faq }))} />
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="category-seo-checklist">
            {checks.map(c => (
              <li key={c.id} className="flex items-start gap-2 text-xs text-gray-700">
                {c.ok ? <CheckCircle2 size={14} className="text-green-600 mt-0.5 flex-shrink-0" /> : c.warn ? <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" /> : <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />}
                <span>{c.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CategorySeoSection;
